import { Monitor, Smartphone } from 'lucide-react'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/utils/cn'

const DESKTOP_WIDTH = 1280
const PHONE_WIDTH = 375

/**
 * Live preview frame. The page lays itself out from its container's width, so the phone view is a
 * 375 px column and the desktop view a 1280 px page scaled down to fit.
 */
export function HomepagePreview({ children }: { children: ReactNode }) {
  const { t } = useTranslation('homepage')
  const [device, setDevice] = useState<'desktop' | 'phone'>('desktop')
  const frame = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(DESKTOP_WIDTH)

  useEffect(() => {
    const el = frame.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setWidth(entry.contentRect.width)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const zoom = device === 'desktop' ? Math.min(1, width / DESKTOP_WIDTH) : 1

  return (
    <section
      aria-labelledby="hp-preview-title"
      className="rounded-card border border-line bg-surface shadow-card"
    >
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-4 py-3">
        <h2 id="hp-preview-title" className="text-[0.9375rem] font-semibold">
          {t('Preview')}
        </h2>
        <div
          role="group"
          aria-label={t('Preview size')}
          className="flex gap-1 rounded-xl border border-line bg-surface-sunken p-1"
        >
          {(
            [
              ['desktop', Monitor, t('Computer')],
              ['phone', Smartphone, t('Phone')],
            ] as const
          ).map(([key, Icon, label]) => (
            <button
              key={key}
              type="button"
              aria-pressed={device === key}
              onClick={() => setDevice(key)}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-medium text-ink-soft',
                device === key ? 'bg-surface text-ink shadow-xs' : 'hover:text-ink',
              )}
            >
              <Icon className="size-3.5" aria-hidden="true" />
              {label}
            </button>
          ))}
        </div>
      </div>
      <div ref={frame} className="bg-surface-sunken p-3">
        <div
          data-testid="homepage-preview"
          // The preview only shows the page: its links and switches must not act on the editor.
          inert
          className={cn(
            'mx-auto h-[70dvh] scrollbar-thin overflow-y-auto rounded-control border border-line bg-canvas shadow-card',
            device === 'phone' ? 'max-w-full' : '',
          )}
          style={{ width: device === 'phone' ? PHONE_WIDTH : '100%' }}
        >
          <div style={device === 'desktop' ? { width: DESKTOP_WIDTH, zoom } : undefined}>{children}</div>
        </div>
      </div>
    </section>
  )
}
